import { lazy, Suspense, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Icon } from '@/shared-ui/atoms';
import { FilterIcon } from '@/shared-ui/icons';
import { SearchResultToggle } from '@/features/product-listing/ui/molecules/search-result-toggle';
import type { PlpFiltersState } from '../../../hooks/use-plp-filters';
import { useInStockFilter } from '../../../hooks/use-in-stock-filter';
import { useFilterOverlayState } from '../../../hooks/use-filter-overlay-state';
import { AppliedFilters, SortMenu } from '../../molecules';

const MobileFilterOverlay = lazy(() => import('../mobile-filter-overlay/mobile-filter-overlay'));

export interface FilterBarProps {
    filters: PlpFiltersState;
    inStockSwitchEnabled: boolean;
    className?: string;
}

const labelClassName = 'text-base leading-5 uppercase tracking-[1.28px] lg:text-[18px] lg:tracking-[1.44px]';

/**
 * Filter + sort row above the listing. On mobile it also carries the pill carousel and the in-stock/count row,
 * and opens the full-screen overlay; on desktop "Filter" is a static heading for the always-visible sidebar.
 */
export const FilterBar = ({ filters, inStockSwitchEnabled, className }: FilterBarProps) => {
    const { t } = useTranslation('plpFilters');
    const inStock = useInStockFilter(filters);
    const overlay = useFilterOverlayState();
    const filterButtonRef = useRef<HTMLButtonElement>(null);

    return (
        <div data-slot="filter-bar" className={cn('flex flex-col gap-4 border-t border-snk-greige py-4', className)}>
            <div className="flex items-start justify-between px-4 sm:px-8 lg:px-16">
                <button
                    type="button"
                    ref={filterButtonRef}
                    onClick={() => overlay.setOpen(true)}
                    aria-haspopup="dialog"
                    className={cn('flex cursor-pointer items-center gap-2 lg:hidden', labelClassName)}>
                    <Icon icon={FilterIcon} decorative className="size-4" />
                    {t('filter')}
                </button>
                <span className={cn('hidden items-center gap-2 lg:flex', labelClassName)}>
                    <Icon icon={FilterIcon} decorative className="size-4" />
                    {t('filter')}
                </span>
                <SortMenu options={filters.sortingOptions} selected={filters.selectedSort} onSelect={filters.setSort} />
            </div>

            <div className="flex flex-col gap-4 lg:hidden">
                <AppliedFilters
                    layout="scroll"
                    className="px-4 sm:px-8"
                    filters={filters.appliedFilters}
                    onRemove={filters.remove}
                    onClearAll={filters.clearAll}
                />
                <SearchResultToggle
                    className="border-t border-snk-greige px-4 pt-4 sm:px-8"
                    itemCount={filters.total}
                    checked={inStock.enabled}
                    onCheckedChange={inStock.setEnabled}
                    showToggle={inStockSwitchEnabled}
                    textLabel={t('inStockOnly')}
                    countLabel={t('results', { count: filters.total })}
                />
            </div>

            {overlay.mounted && (
                <Suspense fallback={null}>
                    <MobileFilterOverlay
                        open={overlay.open}
                        onOpenChange={overlay.setOpen}
                        filters={filters}
                        triggerRef={filterButtonRef}
                    />
                </Suspense>
            )}
        </div>
    );
};
